import { lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorPage from './pages/ErrorPage';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import FeatureBetaGate from './components/FeatureBetaGate';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { refreshStaleChunk } from './utils/chunkRecovery';

function lazyPage(importer) {
  return lazy(() =>
    importer().catch(async (err) => {
      if (await refreshStaleChunk(err)) {
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    })
  );
}

const FunnelPage = lazyPage(() => import('./pages/FunnelPage'));
const DatabasePage = lazyPage(() => import('./pages/DatabasePage'));
const ContasPage = lazyPage(() => import('./pages/ContasPage'));
const TeamPage = lazyPage(() => import('./pages/TeamPage'));
const EsnManagementPage = lazyPage(() => import('./pages/EsnManagementPage'));
const UserManagementPage = lazyPage(() => import('./pages/UserManagementPage'));
const ImportPage = lazyPage(() => import('./pages/ImportPage'));
const ImportRealizadosPage = lazyPage(() => import('./pages/ImportRealizadosPage'));
const ImportTotvsEmailPage = lazyPage(() => import('./pages/ImportTotvsEmailPage'));
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'));
const ForecastPage = lazyPage(() => import('./pages/ForecastPage'));
const SyncPage = lazyPage(() => import('./pages/SyncPage'));
const ProductsPage = lazyPage(() => import('./pages/ProductsPage'));
const MetasPage = lazyPage(() => import('./pages/MetasPage'));
const MetasRealizadoPage = lazyPage(() => import('./pages/MetasRealizadoPage'));
const EsnMetasPage = lazyPage(() => import('./pages/EsnMetasPage'));
const AuditLogPage = lazyPage(() => import('./pages/AuditLogPage'));
const MyAreaPage = lazyPage(() => import('./pages/MyAreaPage'));
const NotificationsPage = lazyPage(() => import('./pages/NotificationsPage'));
const AgendaPage = lazyPage(() => import('./pages/AgendaPage'));
const ReportsPage = lazyPage(() => import('./pages/ReportsPage'));
const ForecastReportPage = lazyPage(() => import('./pages/ForecastReportPage'));
const AutomationsPage = lazyPage(() => import('./pages/AutomationsPage'));
const CrossSellPage = lazyPage(() => import('./pages/CrossSellPage'));
const PlaybooksPage = lazyPage(() => import('./pages/PlaybooksPage'));
const DuplicatesPage = lazyPage(() => import('./pages/DuplicatesPage'));
const WebhooksPage = lazyPage(() => import('./pages/WebhooksPage'));
const CustomFieldsPage = lazyPage(() => import('./pages/CustomFieldsPage'));
const WinLossPage = lazyPage(() => import('./pages/WinLossPage'));
const ProposalPortalPage = lazyPage(() => import('./pages/ProposalPortalPage'));
const ChurnPage = lazyPage(() => import('./pages/ChurnPage'));
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'));
const FeatureFlagsPage = lazyPage(() => import('./pages/FeatureFlagsPage'));
const ApiKeyAdminPage = lazyPage(() => import('./pages/admin/ApiKeyAdminPage'));
const NotificationsAdminPage = lazyPage(() => import('./pages/admin/NotificationsAdminPage'));
const ReconciliacaoPage = lazyPage(() => import('./pages/ReconciliacaoPage'));
const RottingConfigPage = lazyPage(() => import('./pages/admin/RottingConfigPage'));
const RequiredFieldsPage = lazyPage(() => import('./pages/admin/RequiredFieldsPage'));
const MenuConfigPage = lazyPage(() => import('./pages/admin/MenuConfigPage'));
const ApnDashboardPage = lazyPage(() => import('./pages/ApnDashboardPage'));
const ApnMetricsPage = lazyPage(() => import('./pages/ApnMetricsPage'));
const BillingPage = lazyPage(() => import('./pages/BillingPage'));
const SignupPage = lazyPage(() => import('./pages/SignupPage'));
const LegalPage = lazyPage(() => import('./pages/LegalPage'));
const EsnPerformancePage = lazyPage(() => import('./pages/EsnPerformancePage'));
const LeadOriginsPage = lazyPage(() => import('./pages/LeadOriginsPage'));

function PageSkeleton() {
  return (
    <div className="flex items-center justify-center h-full min-h-[40vh]" role="status" aria-label="Carregando página">
      <div className="w-8 h-8 border-4 border-gray-200 border-t-[#00DBFF] rounded-full animate-spin" />
    </div>
  );
}

// Adaptive default redirect based on role
function DefaultRedirect() {
  const { user } = useAuth();
  if (user?.role === 'esn') return <Navigate to="/my-area" replace />;
  return <Navigate to="/dashboard" replace />;
}

function FeatureFlagRoute({ flagKey, featureName, children }) {
  const { enabled, loading } = useFeatureFlag(flagKey);
  if (loading) return null;
  if (!enabled) return <FeatureBetaGate featureName={featureName} />;
  return children;
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <AuthProvider>
        <ToastProvider>
        <BrowserRouter>
          <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/proposta/:token" element={<ProposalPortalPage />} />
            <Route path="/perfil" element={<ProfilePage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Suspense fallback={<PageSkeleton />}>
                    <Routes>
                      <Route path="/my-area" element={<MyAreaPage />} />
                      <Route path="/agenda" element={<AgendaPage />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/reports" element={
                        <ProtectedRoute requiredRole="manager"><ReportsPage /></ProtectedRoute>
                      } />
                      <Route path="/reports/forecast" element={
                        <ProtectedRoute requiredRole="manager"><ForecastReportPage /></ProtectedRoute>
                      } />
                      <Route path="/win-loss" element={
                        <ProtectedRoute requiredRole="manager"><WinLossPage /></ProtectedRoute>
                      } />
                      <Route path="/forecast" element={
                        <ProtectedRoute requiredRole="manager"><ForecastPage /></ProtectedRoute>
                      } />
                      <Route path="/new-names" element={
                        <ProtectedRoute requiredFunnel="new_names"><FunnelPage funnelType="new_names" /></ProtectedRoute>
                      } />
                      <Route path="/base" element={
                        <ProtectedRoute requiredFunnel="base"><FunnelPage funnelType="base" /></ProtectedRoute>
                      } />
                      <Route path="/database" element={
                        <ProtectedRoute requiredRole="manager"><DatabasePage /></ProtectedRoute>
                      } />
                      <Route path="/contas" element={<ContasPage />} />
                      <Route path="/churn" element={
                        <ProtectedRoute requiredRole="manager"><ChurnPage /></ProtectedRoute>
                      } />
                      <Route path="/esn-performance" element={
                        <ProtectedRoute requiredRole="manager"><EsnPerformancePage /></ProtectedRoute>
                      } />
                      <Route path="/esns" element={<EsnMetasPage />} />
                      <Route path="/team" element={
                        <ProtectedRoute requiredRole="admin"><TeamPage /></ProtectedRoute>
                      } />
                      <Route path="/esns-admin" element={
                        <ProtectedRoute requiredRole="admin"><EsnManagementPage /></ProtectedRoute>
                      } />
                      <Route path="/users" element={
                        <ProtectedRoute requiredRole="admin"><UserManagementPage /></ProtectedRoute>
                      } />
                      <Route path="/import" element={
                        <ProtectedRoute requiredRole="manager"><ImportPage /></ProtectedRoute>
                      } />
                      <Route path="/import/realizados" element={
                        <ProtectedRoute requiredRole="manager"><ImportRealizadosPage /></ProtectedRoute>
                      } />
                      <Route path="/import/totvs-email" element={
                        <ProtectedRoute requiredRole="esn"><ImportTotvsEmailPage /></ProtectedRoute>
                      } />
                      <Route path="/sync" element={
                        <ProtectedRoute requiredRole="manager"><SyncPage /></ProtectedRoute>
                      } />
                      <Route path="/products" element={
                        <ProtectedRoute><ProductsPage /></ProtectedRoute>
                      } />
                      <Route path="/metas" element={
                        <ProtectedRoute requiredRole="manager"><MetasPage /></ProtectedRoute>
                      } />
                      <Route path="/metas-realizado" element={
                        <ProtectedRoute><MetasRealizadoPage /></ProtectedRoute>
                      } />
                      <Route path="/automations" element={
                        <ProtectedRoute requiredRole="manager"><AutomationsPage /></ProtectedRoute>
                      } />
                      <Route path="/cross-sell" element={
                        <ProtectedRoute requiredRole="esn"><CrossSellPage /></ProtectedRoute>
                      } />
                      <Route path="/playbooks" element={
                        <ProtectedRoute requiredRole="esn"><PlaybooksPage /></ProtectedRoute>
                      } />
                      <Route path="/duplicates" element={
                        <ProtectedRoute requiredRole="manager"><DuplicatesPage /></ProtectedRoute>
                      } />
                      <Route path="/webhooks" element={
                        <ProtectedRoute requiredRole="admin"><WebhooksPage /></ProtectedRoute>
                      } />
                      <Route path="/custom-fields" element={
                        <ProtectedRoute requiredRole="admin"><CustomFieldsPage /></ProtectedRoute>
                      } />
                      <Route path="/audit-log" element={
                        <ProtectedRoute requiredRole="admin"><AuditLogPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/feature-flags" element={
                        <ProtectedRoute requiredRole="admin"><FeatureFlagsPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/api-keys" element={
                        <ProtectedRoute requiredRole="admin"><ApiKeyAdminPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/notifications" element={
                        <ProtectedRoute><NotificationsAdminPage /></ProtectedRoute>
                      } />
                      <Route path="/reconciliacao" element={
                        <ProtectedRoute><ReconciliacaoPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/reconciliacao" element={
                        <ProtectedRoute requiredRole="manager"><ReconciliacaoPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/rotting-config" element={
                        <ProtectedRoute requiredRole="manager"><RottingConfigPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/required-fields" element={
                        <ProtectedRoute requiredRole="manager"><RequiredFieldsPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/lead-origins" element={
                        <ProtectedRoute requiredRole="manager"><LeadOriginsPage /></ProtectedRoute>
                      } />
                      <Route path="/admin/menu" element={
                        <ProtectedRoute requiredRole="admin"><MenuConfigPage /></ProtectedRoute>
                      } />
                      <Route path="/billing" element={
                        <ProtectedRoute requiredRole="admin"><BillingPage /></ProtectedRoute>
                      } />
                      <Route path="/apn/dashboard" element={
                        <ProtectedRoute>
                          <FeatureFlagRoute flagKey="apn_workspace" featureName="APN Workspace">
                            <ApnDashboardPage />
                          </FeatureFlagRoute>
                        </ProtectedRoute>
                      } />
                      <Route path="/apn/metrics" element={
                        <ProtectedRoute>
                          <FeatureFlagRoute flagKey="apn_workspace" featureName="APN Workspace">
                            <ApnMetricsPage />
                          </FeatureFlagRoute>
                        </ProtectedRoute>
                      } />
                      <Route path="*" element={<DefaultRedirect />} />
                    </Routes>
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
